import React, { FC, useContext, useEffect, useState } from "react";
import ConfirmationModal from "./ConfirmationModal";
import { Autocomplete, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import useLookupsStore, { ITranslationType } from "./../../../common/store/LookupsStore";
import { LanguageContext } from "common/contexts/LanguageContext";
import { matchPath, useHistory } from "react-router-dom";
import { DialogAutoCompleteTextField } from "./DialogTextField";

export interface IChangeLanguageModalProps {
  maxWidth: any;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  handleConfirm: any;
  handleClose: () => void;
  testId?: string;
}

const ChangeLanguageModal: FC<IChangeLanguageModalProps> = (props: IChangeLanguageModalProps) => {
  const { getString } = useContext(LanguageContext);

  const translationTypes = useLookupsStore((state) => state.translationTypes);
  const countries = useLookupsStore((state) => state.countries);
  const history = useHistory();

  const [value, setValue] = useState<string | null>(null);
  const [countryLanguages, setCountryLanguages] = useState<ITranslationType[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<ITranslationType>({
    // Need to set a bogus language or else MUI will complain about changing from uncontrolled value state of a RadioGroup to be controlled (null or undefined cause this)
    translationTypeID: 0,
    translationTypeLanguageName: "",
    translationTypeLocaleName: "",
    translationTypeDesc: "",
    isDefault: false,
    countryID: 0,
  });

  useEffect(() => {
    // Find the first language that is specified as default
    const defaultLanguage = countryLanguages.find((language) => language.isDefault);
    if (defaultLanguage !== undefined) {
      setSelectedLanguage(defaultLanguage);
    }
  }, [countryLanguages]);

  const handleCountryChange = (event: React.SyntheticEvent, newValue: string | null) => {
    setValue(newValue);
    // Find the selected country and set its languages as the new options
    const selectedCountry = countries.find((country) => country.countryName === newValue);
    if (selectedCountry) {
      setCountryLanguages(translationTypes.filter((t) => t.countryID === selectedCountry.countryID));
    } else {
      setCountryLanguages([]);
    }
  };

  const handleLanguageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLanguage = countryLanguages.find(
      (countryLanguage) => countryLanguage.translationTypeLanguageName === event.target.value,
    );
    if (newLanguage) {
      setSelectedLanguage(newLanguage);
      console.log(`selected locale: ${selectedLanguage.translationTypeLocaleName}`);
    }
  };

  const updateLocaleInPath = (path: string, newLocale: string): string => {
    const match = matchPath<{ locale?: string; path?: string }>(path, {
      path: "/:locale?/:path*",
    });

    const { path: remainingPath } = match?.params || {};
    const newPath = newLocale ? `/${newLocale}/${remainingPath || ""}` : `/${remainingPath || ""}`;

    return newPath;
  };

  const handleConfirmation = () => {
    props.setIsOpen(false);
    const currentPath = window.location.pathname;
    const newPath = updateLocaleInPath(currentPath, selectedLanguage.translationTypeLocaleName);
    history.push(newPath);
  };

  return (
    <ConfirmationModal
      hideCancel={true}
      isOpen={props.isOpen}
      handleClose={() => props.setIsOpen(false)}
      title={getString("dlg_lang_title")}
      confirmBtnLabel={getString("dlg_lang_confirmbtn")}
      confirmBtnIcon={ChevronRightOutlinedIcon}
      maxWidth={props.maxWidth}
      handleConfirm={handleConfirmation}
      hideDivider={true}
      buttonsJustify="center"
    >
      <>
        <Autocomplete
          sx={{ mt: "16px" }}
          options={countries.map((country) => country.countryName)}
          value={value}
          onChange={handleCountryChange}
          renderInput={(params) => (
            <DialogAutoCompleteTextField
              {...params}
              label={getString("dlg_lang_country")}
              variant="filled"
              size="small"
              hiddenLabel
              InputLabelProps={{ shrink: true }}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                endAdornment: (
                  <>
                    {params.InputProps.endAdornment}
                    <SearchIcon />
                  </>
                ),
              }}
            />
          )}
        />
        {countryLanguages.length > 0 && (
          <>
            <Typography
              variant="body2"
              sx={{
                mt: "20px",
                mb: "-6px",
                textTransform: "uppercase",
                color: "black",
                fontSize: "0.75rem",
                fontWeight: "600",
              }}
            >
              {getString("dlg_lang_language")}
            </Typography>
            <RadioGroup onChange={handleLanguageChange} value={selectedLanguage.translationTypeLanguageName}>
              {countryLanguages.map((countryLanguage, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Radio
                      disableFocusRipple
                      disableRipple
                      value={countryLanguage.translationTypeLanguageName}
                      sx={{
                        // Grey border for the default state
                        borderColor: "grey",
                        // Light gray interior on hover
                        "&:hover": {
                          borderColor: "grey",
                        },
                        // Green interior on selected
                        "&.Mui-checked": {
                          color: "primary.light",
                        },
                      }}
                    />
                  }
                  label={countryLanguage.translationTypeLanguageName}
                  sx={{ mb: "-10px" }}
                />
              ))}
            </RadioGroup>
          </>
        )}
      </>
    </ConfirmationModal>
  );
};

export default ChangeLanguageModal;
